import React from 'react';
import classnames from 'classnames';
import { defineMessages, FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Button } from '@dropbox/dig-components/buttons';
import { UIIcon } from '@dropbox/dig-icons';
import {
  FileRequestLine,
  SendLine,
  SignatureLine,
} from '@dropbox/dig-icons/assets';

import HfReactHelper from 'js/sign-components/common/hf-react-helper';
import type { PropsWithSiteCode } from 'js/sign-components/common/brand';
import {
  getSupportEmailAddress,
  siteCodes,
} from 'js/sign-components/common/brand';
import { useFeatureFlag } from 'js/sign-components/common/feature-flag';
import { useIsMobile } from 'js/sign-components/common/hooks';
import { createBannerMessage } from 'hellospa/components/notification-banner/data/actions';
import { NotificationBannerType } from 'hellospa/components/notification-banner/data/types';
import { GlobalNavTourItems } from 'hellospa/components/global-nav/global-nav-onboarding-tour';
import BulkSendModal from './bulk-send-modal';
import BulkSendTooltip from './bulk-send-tooltip';
import styles from './primary-actions.scss';

type PrimaryActionsProps = PropsWithSiteCode<{
  canAccessBulkSendUI: boolean;
  isSandBoxedLeoUser?: boolean;
  placement?: 'sidebar' | 'contentHeader';
}>;

const messages = defineMessages({
  bulkSendUnavailable: {
    id: 'bulk_send_unavailable',
    description:
      'notification banner if access to Bulk Send functionality is not allowed',
    defaultMessage:
      'Bulk Send is currently unavailable for your account. Please contact us at {supportEmail} if you need access or have questions.',
  },
});

export function PrimaryActions({
  siteCode,
  canAccessBulkSendUI,
  isSandBoxedLeoUser,
  placement = 'sidebar',
}: PrimaryActionsProps) {
  const site = HfReactHelper.HfSites.getSite(siteCode);
  const isSign = HfReactHelper.HfSites.isHelloSign(site);
  const isFax = HfReactHelper.HfSites.isHelloFax(site);
  const isMobile = useIsMobile();
  const [isBulkSendModalOpen, setIsBulkSendUpgradeModalOpen] =
    React.useState(false);
  const blockBulkSendSpamRisk = useFeatureFlag('block_bulk_spam_risk');
  const enableBulkSendTrial = useFeatureFlag(
    'sign_systems_2026_01_06_bulk_send_trial_rollout',
  );
  const dispatch = useDispatch();
  const supportEmail = getSupportEmailAddress(siteCodes.sign);
  const bulkSendTriggerRef = React.useRef<HTMLDivElement>(null);
  const isContentHeader = placement === 'contentHeader';
  const buttonSize = isContentHeader ? 'medium' : 'large';

  const onBulkSendPressed = React.useCallback(
    (evt) => {
      if (enableBulkSendTrial) {
        if (blockBulkSendSpamRisk) {
          return;
        }

        if (!canAccessBulkSendUI) {
          evt.preventDefault();
          setIsBulkSendUpgradeModalOpen(true);
        }
        return;
      }

      if (blockBulkSendSpamRisk) {
        evt.preventDefault();
        dispatch(
          createBannerMessage(
            messages.bulkSendUnavailable,
            NotificationBannerType.Err,
            { supportEmail },
            { timeout: 7500 },
          ),
        );
        return;
      }

      if (!canAccessBulkSendUI) {
        evt.preventDefault();
        setIsBulkSendUpgradeModalOpen(true);
      }
    },
    [
      blockBulkSendSpamRisk,
      canAccessBulkSendUI,
      dispatch,
      enableBulkSendTrial,
      supportEmail,
    ],
  );

  const signDocumentsButton = isSign ? (
    <Button
      variant="primary"
      size={buttonSize}
      data-qa-ref="global-nav-sign-documents-btn"
      data-tour-item={GlobalNavTourItems.signDocuments}
      href={HfReactHelper.urlHelper('prep-and-send', site)}
      withIconLeft={
        isContentHeader ? <UIIcon src={SignatureLine} /> : undefined
      }
    >
      <FormattedMessage
        id=""
        description="Text for a button which redirects user to the Sign Documents page when pressed."
        defaultMessage="Sign documents"
      />
    </Button>
  ) : null;

  const bulkSendButton =
    isSign && !isSandBoxedLeoUser ? (
      <>
        <Button
          variant="opacity"
          size={buttonSize}
          data-qa-ref="global-nav-bulk-send-btn"
          href={HfReactHelper.urlHelper('prep-and-send/new/13', site)}
          onClick={onBulkSendPressed}
          ref={bulkSendTriggerRef}
          withIconLeft={
            isContentHeader ? <UIIcon src={FileRequestLine} /> : undefined
          }
        >
          <FormattedMessage
            id=""
            description="Text for a button which redirects user to the Sign Documents page when pressed."
            defaultMessage="Bulk send"
          />
        </Button>
        {!isMobile && canAccessBulkSendUI && !blockBulkSendSpamRisk && (
          <BulkSendTooltip triggerRef={bulkSendTriggerRef} />
        )}
        {!canAccessBulkSendUI && (
          <BulkSendModal
            open={isBulkSendModalOpen}
            onRequestClose={() => setIsBulkSendUpgradeModalOpen(false)}
          />
        )}
      </>
    ) : null;

  return (
    <div
      className={classnames(styles.primaryActions, styles[placement])}
      data-testid={isContentHeader ? 'home-primary-actions' : undefined}
    >
      {isContentHeader ? (
        <>
          {bulkSendButton}
          {signDocumentsButton}
        </>
      ) : (
        <>
          {signDocumentsButton}
          {bulkSendButton}
        </>
      )}
      {isFax && (
        <Button
          variant="primary"
          size={buttonSize}
          data-qa-ref="global-nav-send-fax-btn"
          href={HfReactHelper.urlHelper('prep-and-send', site)}
          withIconLeft={isContentHeader ? <UIIcon src={SendLine} /> : undefined}
        >
          <FormattedMessage
            id=""
            description="Text for a button which redirects user to the Send a Fax page when pressed."
            defaultMessage="Send faxes"
          />
        </Button>
      )}
    </div>
  );
}
